import { useState } from 'react';
import SafeIcon from './SafeIcon';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { DEMO_NOW, DEMO_TODAY, LATE_WINDOW_MINUTES } from '../store/seed';

const MAX_LISTED_UPCOMING_POSTS = 8;
const RESUME_TIME_OPTIONS = ['10:30', '11:30', '18:30', '19:45'];

const minutesSinceMidnight = (clockTime: string) => {
  const [hours, minutes] = clockTime.split(':').map(Number);
  return hours * 60 + minutes;
};

type MissedPostOutcome = 'neverPublishLate' | 'late' | 'tooLate';

export default function PauseDialog({
  mode,
  onClose,
}: {
  mode: 'pause' | 'resume';
  onClose: () => void;
}) {
  const { translate, language } = useApp();
  const { route, actor, notify } = useUi();
  const { commands } = useDemo();
  const { workspace, posts } = useWorkspaceData(route.workspaceId);
  const [resumeAt, setResumeAt] = useState('11:30');
  const formatDay = (dayOfMonth: number) =>
    new Intl.DateTimeFormat(language, { weekday: 'short', day: 'numeric', month: 'short' }).format(
      new Date(2026, 10, dayOfMonth)
    );
  const upcomingScheduledPosts = posts
    .filter(
      (post) =>
        post.status === 'Scheduled' &&
        (post.day > DEMO_TODAY || (post.day === DEMO_TODAY && post.time >= DEMO_NOW))
    )
    .sort(
      (firstPost, secondPost) =>
        firstPost.day - secondPost.day || firstPost.time.localeCompare(secondPost.time)
    );
  const postsMissedDuringPause = posts.filter(
    (post) =>
      post.status === 'Scheduled' &&
      post.day === DEMO_TODAY &&
      post.time >= DEMO_NOW &&
      post.time < resumeAt
  );
  const minutesLateAtResume = (scheduledTime: string) =>
    minutesSinceMidnight(resumeAt) - minutesSinceMidnight(scheduledTime);
  const missedPostOutcome = (
    scheduledTime: string,
    neverPublishLate?: boolean
  ): MissedPostOutcome => {
    if (neverPublishLate) return 'neverPublishLate';
    return minutesLateAtResume(scheduledTime) <= LATE_WINDOW_MINUTES ? 'late' : 'tooLate';
  };
  const missedPostOutcomeMessage = (outcome: MissedPostOutcome, scheduledTime: string) => {
    if (outcome === 'late')
      return translate('Goes out now, {m} minutes late', {
        m: minutesLateAtResume(scheduledTime),
      });
    if (outcome === 'neverPublishLate')
      return translate('Marked Do not publish late: needs a new time');
    return translate('More than two hours late: needs a new time');
  };

  function pausePublishing() {
    commands.setPaused(workspace.id, true, actor);
    notify(translate('Publishing paused for {ws}.', { ws: workspace.name }));
    onClose();
  }

  function resumePublishing() {
    const resumeResults = commands.resumePublishing(workspace.id, resumeAt, actor);
    const postsNeedingNewTimeCount = resumeResults.filter(
      (resumeResult) => !resumeResult.canPublishLate
    ).length;
    notify(
      postsNeedingNewTimeCount
        ? translate('Publishing resumed. {n} posts need a new time.', {
            n: postsNeedingNewTimeCount,
          })
        : translate('Publishing resumed.'),
      postsNeedingNewTimeCount ? 'warn' : undefined
    );
    onClose();
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="panel modal-card pause-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pause-title"
      >
        {mode === 'pause' ? (
          <>
            <h2 id="pause-title">
              {translate('Pause all publishing for {ws}?', { ws: workspace.name })}
            </h2>
            <p>
              {translate(
                'Nothing is published until you resume. Drafts and reviews continue. Demo clock: {time} today.',
                { time: DEMO_NOW }
              )}
            </p>
            <h3>
              {translate('{n} scheduled posts would wait', { n: upcomingScheduledPosts.length })}
            </h3>
            <ul className="bulk-list">
              {upcomingScheduledPosts.slice(0, MAX_LISTED_UPCOMING_POSTS).map((post) => (
                <li key={post.id}>
                  <SafeIcon name="FiClock" size={14} />
                  <span>
                    <strong>{post.title}</strong> · {post.channel} · {formatDay(post.day)}{' '}
                    {post.time}
                  </span>
                  {post.neverPublishLate && (
                    <span className="muted small-print">{translate('Do not publish late')}</span>
                  )}
                </li>
              ))}
            </ul>
            {upcomingScheduledPosts.length > MAX_LISTED_UPCOMING_POSTS && (
              <p className="muted small-print">
                {translate('…and {n} more', {
                  n: upcomingScheduledPosts.length - MAX_LISTED_UPCOMING_POSTS,
                })}
              </p>
            )}
            <p className="muted small-print">
              {translate(
                'After resuming, posts missed by up to two hours go out late, unless marked Do not publish late. Anything later needs a new time.'
              )}
            </p>
            <div className="button-row">
              <button className="button danger" onClick={pausePublishing}>
                <SafeIcon name="FiPauseCircle" />
                {translate('Pause publishing')}
              </button>
              <button className="button" onClick={onClose}>
                {translate('Cancel')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="pause-title">{translate('Resume publishing')}</h2>
            <label className="wizard-field">
              {translate('Resume time (demo clock; the pause started at {time})', {
                time: DEMO_NOW,
              })}
              <select
                className="select-field"
                value={resumeAt}
                onChange={(event) => setResumeAt(event.target.value)}
              >
                {RESUME_TIME_OPTIONS.map((resumeTimeOption) => (
                  <option key={resumeTimeOption}>{resumeTimeOption}</option>
                ))}
              </select>
            </label>
            {postsMissedDuringPause.length === 0 ? (
              <p className="muted">{translate('Nothing was missed during the pause.')}</p>
            ) : (
              <>
                <h3>{translate('Missed during the pause')}</h3>
                <ul className="bulk-list">
                  {postsMissedDuringPause.map((post) => {
                    const outcome = missedPostOutcome(post.time, post.neverPublishLate);
                    const goesOutLate = outcome === 'late';
                    return (
                      <li key={post.id}>
                        <SafeIcon name={goesOutLate ? 'FiSend' : 'FiAlertTriangle'} size={14} />
                        <span>
                          <strong>{post.title}</strong> · {post.channel} · {post.time}
                        </span>
                        <span className={goesOutLate ? 'muted small-print' : 'field-warn'}>
                          {missedPostOutcomeMessage(outcome, post.time)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            <div className="button-row">
              <button className="button primary" onClick={resumePublishing}>
                <SafeIcon name="FiPlayCircle" />
                {translate('Resume at {time}', { time: resumeAt })}
              </button>
              <button className="button" onClick={onClose}>
                {translate('Cancel')}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
